import { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";
import { isIOS, isStandalone, isSafariBrowser } from "@/lib/platform";

const DISMISS_KEY = "sonspeak_ios_install_dismissed";

/** iOS Safari has no beforeinstallprompt event — show a contextual help card
 * the first time an iOS user visits, with the actual Add-to-Home-Screen steps. */
export default function IosInstallCard() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissed = (() => {
      try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
    })();
    if (dismissed) return;
    if (!isIOS()) return;
    if (!isSafariBrowser()) return;
    if (isStandalone()) return;
    // Show after a short delay so the page renders first
    const t = setTimeout(() => setShow(true), 1800);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setShow(false);
  };

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto sm:max-w-md brutal-card bg-white p-4 z-40"
      data-testid="ios-install-card"
      role="dialog"
      aria-label="Install SonSpeak on this iPad"
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl shrink-0" aria-hidden>📱</div>
        <div className="flex-1">
          <div className="display text-lg font-extrabold mb-0.5">Install on this iPad</div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3">
            For the best experience — full screen, fast, and works offline.
          </p>
          <ol className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <span className="display font-bold w-5 shrink-0">1.</span>
              <span>Tap <Share size={15} className="inline align-text-bottom" aria-label="Share" strokeWidth={2.5} /> <strong>Share</strong> at the bottom of Safari.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="display font-bold w-5 shrink-0">2.</span>
              <span>Scroll and pick <strong>Add to Home Screen</strong> <Plus size={14} className="inline align-text-bottom" strokeWidth={2.5} />.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="display font-bold w-5 shrink-0">3.</span>
              <span>Tap <strong>Add</strong> — SonSpeak now lives on your home screen.</span>
            </li>
          </ol>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="bg-white border-[3px] border-[color:var(--ink)] rounded-full w-9 h-9 flex items-center justify-center shadow-[2px_2px_0_0_var(--ink)]"
          aria-label="Dismiss"
          data-testid="ios-install-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
